import { useEffect } from 'react';

import SocketService from '../services/socket.service';

/**
 * Custom hook to manage WebSocket connections for various event types
 * @param {Object} options - Configuration options
 * @param {string|Array} options.eventNames - Event name(s) to listen for (default: 'quality-observation-questions-updated')
 * @param {Function} options.onEvent - General callback function when any event is received
 * @param {Object} options.eventHandlers - Object mapping event types to callback functions
 * @param {number} options.entityId - Optional specific entity ID to filter events for
 * @param {string} options.entityIdField - Field name for entity ID in event data (default: 'observationId')
 * @param {Array} options.rooms - Array of room configurations to join
 * @param {boolean} options.joinUserRoom - Whether to join user room (default: true)
 * @param {Array} options.dependencies - Additional dependencies for useEffect
 * @param {boolean} options.enableDeduplication - Whether to deduplicate events (default: true)
 */
const useWebSocketEvents = ({
  eventNames = 'quality-observation-questions-updated',
  onEvent,
  eventHandlers = {},
  entityId,
  entityIdField = 'observationId',
  rooms = [],
  joinUserRoom = true,
  dependencies = [],
  enableDeduplication = true,
}) => {
  useEffect(() => {
    const socket = new SocketService();
    const processedEvents = enableDeduplication ? new Set() : null;

    const handleEvent = (eventData) => {
      const data = Array.isArray(eventData) ? eventData[0] : eventData;

      if (!data) {
        return;
      }

      // Event deduplication
      if (enableDeduplication) {
        const eventId = entityId ? `${data.eventType}-${data[entityIdField]}` : `${data.eventType}-${data.entityId || data[entityIdField]}`;

        if (processedEvents.has(eventId)) {
          return;
        }
        processedEvents.add(eventId);
      }

      // Filter by entity ID if specified
      if (entityId && data[entityIdField] !== entityId) {
        return;
      }

      // Handle specific event types via eventHandlers
      if (data.eventType && eventHandlers[data.eventType]) {
        eventHandlers[data.eventType](data);
      }

      // General event callback
      if (onEvent) {
        onEvent(data);
      }
    };

    // Set up event listeners for multiple event names
    const eventNamesArray = Array.isArray(eventNames) ? eventNames : [eventNames];
    eventNamesArray.forEach((eventName) => {
      socket.socket.on(eventName, handleEvent);
    });

    // Join user room by default
    if (joinUserRoom) {
      socket.sendMessage('join-user-room', {});
    }

    // Join additional rooms
    rooms.forEach((room) => {
      if (typeof room === 'string') {
        socket.sendMessage(room, {});
      } else if (room.message && room.data) {
        socket.sendMessage(room.message, room.data);
      }
    });

    // Cleanup function
    return () => {
      eventNamesArray.forEach((eventName) => {
        socket.socket.off(eventName, handleEvent);
      });
      socket.disconnect();
    };
  }, [entityId, entityIdField, eventNames, onEvent, eventHandlers, rooms, joinUserRoom, enableDeduplication, ...dependencies]);
};

const useQualityObservationSocket = (options) => {
  const { observationId, joinObservationRoom, onCreated, onFailed, onSuccess, onUpdate, dependencies = [] } = options;

  const eventHandlers = {};
  if (onCreated) eventHandlers.created = onCreated;
  if (onFailed) eventHandlers.failed = onFailed;
  if (onSuccess) eventHandlers.updated = onSuccess;

  const rooms = [];
  if (joinObservationRoom && observationId) {
    rooms.push({
      data: { observationId: Number(observationId) },
      message: 'join-observation-room',
    });
  }

  return useWebSocketEvents({
    dependencies,
    entityId: observationId,
    entityIdField: 'observationId',
    eventHandlers,
    onEvent: onUpdate,
    rooms,
  });
};

export default useWebSocketEvents;

export { useWebSocketEvents, useQualityObservationSocket };
